import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Switch, Typography } from "@mui/material";
import React, { memo, useCallback, useMemo, useState } from "react";
import "./CookieConsent.css";

interface CookieConsentProps {
    children: React.ReactNode;
}

const CookieConsent = memo((props: CookieConsentProps) => {
    const cookieName = "cookieConsent";
    const cookie = getCookie(cookieName);
    const cookiesAccepted = Boolean(cookie);
    const defaultAgreements = useMemo(() => ({ analytics: false, marketing: false, settings: false, necessary: true }), []);
    const [dialogState, setDialogState] = useState<CookieDialogState>({
        open: !cookiesAccepted,
        showCookieSettings: false,
        agreements: { ...defaultAgreements },
    });
    const cookieTypes = Object.keys(dialogState.agreements) as unknown as CookieSettingTypes[];
    const closeDialog = useCallback(() => setDialogState((p) => ({ ...p, open: false })), []);
    const changeCookieConsentSelected = useCallback(
        (settingName: string, isSelected: boolean) => setDialogState((p) => ({ ...p, agreements: { ...p.agreements, [settingName]: isSelected } })),
        []
    );
    const eraseCookie = useCallback(() => {
        const rejectedAgreements = defaultAgreements;
        document.cookie = `${cookieName}=${JSON.stringify(rejectedAgreements)}; expires=Fri, 31 Dec 9999 23:59:59 GMT; path=/`;
        closeDialog();
    }, [closeDialog, defaultAgreements]);
    const onReject = useCallback(() => {
        closeDialog();
        eraseCookie();
    }, [closeDialog, eraseCookie]);
    const onAccept = useCallback(() => {
        const acceptedCookies = cookieTypes.map((key) => `${key}: true`).join(",");
        document.cookie = `${cookieName}=${JSON.stringify(acceptedCookies)}; expires=Fri, 31 Dec 9999 23:59:59 GMT; path=/`;
        closeDialog();
    }, [closeDialog, cookieTypes]);
    const onViewCookies = useCallback(() => setDialogState((p) => ({ ...p, showCookieSettings: true })), []);
    const onCookiesSave = useCallback(() => {
        const cookieSettings = cookieTypes
            .map((key: CookieSettingTypes) => {
                if (typeof dialogState.agreements[key] === "boolean") {
                    return `${key}: ${dialogState.agreements[key]}`;
                }
                return "";
            })
            .filter(Boolean)
            .join(",");
        document.cookie = `${cookieName}=${JSON.stringify(cookieSettings)}; expires=Fri, 31 Dec 9999 23:59:59 GMT; path=/`;
        closeDialog();
    }, [closeDialog, cookieTypes, dialogState.agreements]);

    return (
        <>
            {props.children}
            <Dialog maxWidth="sm" open={dialogState.open}>
                <DialogTitle variant="h4">Cookies on Public Portal</DialogTitle>
                {dialogState.showCookieSettings ? (
                    <CookiesConsentDialogViewCookieSettings changeCookieConsentSelected={changeCookieConsentSelected} agreements={dialogState.agreements} onSave={onCookiesSave} />
                ) : (
                    <CookiesConsentDialogDetails onAccept={onAccept} onReject={onReject} onViewCookies={onViewCookies} />
                )}
            </Dialog>
        </>
    );

    function getCookie(name: string) {
        const matches = document.cookie.match(new RegExp("(?:^|; )" + name.replace(/([.$?*|{}()[\]\\/+^])/g, "\\$1") + "=([^;]*)"));
        return matches ? decodeURIComponent(matches[1]) : undefined;
    }
});

const CookiesConsentDialogDetails = memo((props: CookiesConsentDialogDetailsProps) => {
    const { onReject, onAccept, onViewCookies } = props;
    return (
        <>
            <DialogContent>We use cookies to make the website work as well as possible.</DialogContent>
            <DialogActions>
                <Button onClick={onReject} variant="contained">
                    Reject additional cookies
                </Button>
                <Button onClick={onAccept} variant="contained" color="primary">
                    Accept additional cookies
                </Button>
                <Button onClick={onViewCookies} variant="text" color="primary">
                    View cookies
                </Button>
            </DialogActions>
        </>
    );
});

const CookiesConsentDialogViewCookieSettings = memo((props: CookiesConsentDialogViewCookieSettingsProps) => {
    const { changeCookieConsentSelected, agreements, onSave } = props;
    return (
        <>
            <DialogContent>
                Cookies are files saved on your phone, tablet or computer when you visit a website. We use cookies to store information about how you use the Public Portal website,
                such as the pages you visit.
                <CookieSettingType
                    title="Cookies that measure website use"
                    description="Google Analytics sets cookies that store anonymised information about: how you got to the site the pages you visit on Public Portal, and how long you spend on each page what you click on while you're visiting the site. We don't currently use any cookies in this category."
                    propertyName="analytics"
                    onChange={changeCookieConsentSelected}
                    checked={agreements.analytics}
                />
                <CookieSettingType
                    title="Cookies that help with our communications and marketing"
                    description="These cookies may be set by third party websites. We don't currently use any cookies in this category."
                    propertyName="marketing"
                    onChange={changeCookieConsentSelected}
                    checked={agreements.marketing}
                />
                <CookieSettingType
                    title="Cookies that remember your settings"
                    description="These cookies do things like remember your preferences and the choices you make, to personalise your experience of using the site. We don't currently use any cookies in this category."
                    propertyName="settings"
                    onChange={changeCookieConsentSelected}
                    checked={agreements.settings}
                />
                <CookieSettingType
                    title="Strictly necessary cookies"
                    description="These essential cookies do things like remember your user session, so we don't ask for them again. They always need to be on."
                    propertyName="necessary"
                    disabled={true}
                    checked={true}
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={onSave} variant="contained">
                    Save changes
                </Button>
            </DialogActions>
        </>
    );
});

const CookieSettingType = memo((props: CookieSettingTypeProps) => {
    const { title, description, propertyName, onChange, checked, disabled } = props;
    const onSelectionChange = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            onChange && onChange(event.target.name, event.target.checked);
        },
        [onChange]
    );
    return (
        <Box marginTop="1rem">
            <Box display="flex" justifyContent="space-between">
                <Typography variant="h5">{title}</Typography>
                {propertyName ? <Switch name={propertyName} onChange={onSelectionChange} checked={checked} disabled={disabled} disableRipple /> : null}
            </Box>
            <Typography variant="body1">{description}</Typography>
        </Box>
    );
});

interface CookiesConsentDialogDetailsProps {
    onReject: () => void;
    onAccept: () => void;
    onViewCookies: () => void;
}
interface CookiesConsentDialogViewCookieSettingsProps {
    agreements: CookieSettingAgreement;
    changeCookieConsentSelected: (settingName: string, isSelected: boolean) => void;
    onSave: () => void;
}

interface CookieSettingTypeProps {
    title: string;
    description: string;
    propertyName: string;
    checked: boolean;
    disabled?: boolean;
    onChange?: (settingName: string, isSelected: boolean) => void;
}

interface CookieSettingAgreement {
    analytics: boolean;
    marketing: boolean;
    settings: boolean;
    necessary: boolean;
}

interface CookieDialogState {
    open: boolean;
    showCookieSettings: boolean;
    agreements: CookieSettingAgreement;
}

type CookieSettingTypes = keyof CookieSettingAgreement;

export default CookieConsent;
